import { Dayjs } from "dayjs";
import { AbandonedPreference } from "./AbandonedPreference";
import { AccountBannerPreference } from "./AccountBannerPreference";
import { EmbeddedTheme } from "./ButtonCodePreferences";
import { CancellationProtectionPreference } from "./CancellationProtectionPreference";
import { CheckInPreference } from "./CheckInPreference";
import { ConversionTracking } from "./ConversionTracking";
import { CrossAccountSellingPreference } from "./CrossAccountSellingPreference";
import { EmvDevice } from "./EmvDevice";
import { FacebookPixelTracking } from "./FacebookPixelTracking";
import { FraudPreference } from "./FraudPreference";
import { GooglePreferences } from "./GooglePreferences";
import { GratuityPreference } from "./GratuityPreference";
import { NotificationPreference } from "./NotificationPreference";
import { UserDetailsRequirement } from "./Product";
import { QrCodePreference } from "./QrCodePreference";
import { ReminderPreference } from "./ReminderPreference";
import { ReviewPreference } from "./ReviewPreference";
import { RosterExportPreference } from "./RosterExportPreference";
import { TelemetryPreference } from "./Telemetry";
import { XolaBotPreference } from "./XolaBotPreference";

export interface GuidePermission {
    canModifySchedules: boolean;
    modifySchedulesOffset: number;
}

export interface GuideNotificationPreference {
    guideNotConfirmedToGuide?: ReminderPreference;
    guideNotConfirmedToSeller?: ReminderPreference;
    requireConfirmation: boolean;
    allowDecline?: boolean;
}

export interface GiftPreference {
    enabled: boolean;
    showAdditionalExperiences: boolean;
    customerPhoneRequired: boolean;
    captchaEnabled: boolean;
    allowMultiItemGifts: boolean;
    showDemographics: boolean;
    recipientDetailsRequirement?: UserDetailsRequirement;
    organizerDetailsRequirement?: UserDetailsRequirement;
}

export interface BookingPreference {
    allowMultiplePrivateEvents: boolean;
    orderItemization?: { enabled?: boolean };
    postBookingAction: string;
    allowMultiItemOrders: boolean;
    reservationLimit: number | null;
    allowBookingEngineOrderModification: boolean;
    allowPastDates: boolean;
    timeslotReservationLimit?: number;
    defaultPriceStrategyForPurchaseEdit: string;
}

export interface SupportedLanguage {
    locale: string;
    name: string;
}

export interface LanguagePreference {
    locale: string;
    multiple: boolean;
    supportedLanguages: SupportedLanguage[];
}

export interface UserPreferences {
    affiliate: {
        allowLogin: boolean;
        affiliateCreditLine: {
            enableLowBalanceWarning: boolean;
            enabled: boolean;
            lowBalanceReminderInterval: string;
        };
    };
    availabilityTimeline?: AvailabilityTimelinePreference;
    guideNotification: GuideNotificationPreference;
    inventory: {
        multipleResourcesEnabled: boolean;
        allowSchedules: boolean;
        allowEquipmentAsPurchasableUnit: boolean;
        allowedAllocationStrategies: null;
        allocationStrategy: EquipmentAllocationStrategy;
        prioritizedResources?: PrioritizedResourceID[];
    };
    language: LanguagePreference;
    guidePermission: GuidePermission;
    google: GooglePreferences;
    requireConfirmation: boolean;
    communicationChannels?: { enabledChannels: string[] };
    checkIn: CheckInPreference;
    booking: BookingPreference;
    payment: {
        enforcePostalCode: boolean;
        threeDSecureEnabled: boolean;
        requireAdditionalBillingInfo: boolean;
        taxAssessedOn: TaxAssessedOn;
        fraud?: FraudPreference;
    };
    conversionTrackings: ConversionTracking[];
    facebookPixelTracking: FacebookPixelTracking;
    xolabot: XolaBotPreference;
    abandoned: AbandonedPreference;
    gift?: GiftPreference;
    cancellationProtection?: CancellationProtectionPreference;
    rosterExport: RosterExportPreference;
    roster: RosterPreference;
    notifications: NotificationPreference[];
    ticket: {
        autoCheckIn: boolean;
        autoPrint: boolean;
        enabled: boolean;
        template: string;
        checkInRules?: CheckInRule[];
    };
    stripeTerminal: StripeTerminal;
    gratuity: GratuityPreference;
    payout: {
        escrow?: EscrowPreference;
    };
    splitPayment?: SplitPaymentPreference;
    checkout?: CheckoutPreference;
    crossAccountSelling?: CrossAccountSellingPreference;
    datepickerPreference: DatepickerPreference;
    waiver: WaiverPreference;
    qrCode?: QrCodePreference;
    review?: ReviewPreference;
    multiFactorAuthentication?: MultiFactorAuthentication;
    telemetry?: TelemetryPreference;
    network?: NetworkPreferences;
    accountBanner?: AccountBannerPreference;
    customStyle?: any;
    dataRetention?: DataRetentionPreference;
    autoLogout?: AutoLogoutPreference;
}

export interface NetworkPreferences {
    enabled: boolean;
    aboutBusiness: string;
    email?: string;
    phone?: string;
    visibilityType: NetworkVisibilityType;
}

export enum NetworkVisibilityType {
    VISIBILITY_ALL = "all",
}

export interface WaiverPreference {
    multiLinkDayInterval?: string;
}

export interface AutoLogoutPreference {
    enabled: boolean;
    idleMinutes: number;
}

export interface DataRetentionPreference {
    enabled: boolean;
    duration: string | null;
}

export interface SplitPaymentPreference {
    isEnabled: boolean;
    autoChargeOnDeadline: boolean;
    paymentDeadline?: number;
}

export type FLOW = "mvp" | "mvp_calendar" | "single_page";
export interface CheckoutPreference {
    displayEventDuration: boolean;
    footerLinks: string[];
    includeFees: boolean;
    priceDisplay?: CheckoutPriceDisplay;
    showTaxesAndFeesBreakdown: boolean;
    theme?: ThemePreference;
    x2Probability?: number;
    captchaEnabled?: boolean;
    flowWeights?: Record<string, number>;
}

export enum CheckoutPriceDisplay {
    LOWEST = "lowest",
    HIDE = "hide",
    RANGE = "range",
    BASE = "base",
}

export interface AvailabilityTimelinePreference {
    maxDays: number;
    showUnavailable?: boolean;
    showPrice?: boolean;
    showOpen?: boolean;
    showOpenMax?: string;
    unavailableMessage?: string | null;
    showUnavailableMessage?: boolean;
    showExcerpt?: boolean;
}

export interface ThemePreference {
    name?: EmbeddedTheme;
    themeUrl: string;
    // The variable will always be present but name can be missing
    themeVariables: ThemeVariables[];
}

export interface ThemeVariables {
    name: ThemeVariablesName;
    value: string;
}

export enum ThemeVariablesName {
    ACCENT_COLOR = "accentColor",
}

export interface EscrowPreference {
    actualAmount: number;
    desiredAmountFloor: number;
    desiredAmount: number;
    minimumTransactionCreatedAt?: Dayjs;
}

export interface CheckInRule {
    id: ID;
    name: string;
    showOnTicket: boolean;
    required: boolean;
    passText?: string;
    failText?: string;
}

export interface StripeTerminal {
    location: StripeTerminalLocation;
    paymentDevices: EmvDevice[];
}

export interface StripeTerminalLocation {
    id: ID;
    remoteId: string;
}

export interface RosterPreference {
    printNotes: "all" | "none";
}

export enum TaxAssessedOn {
    PROMOTIONAL_VALUE = "promotional_value",
    PURCHASE_VALUE = "purchase_value",
}

export interface DatepickerPreference {
    displayCountMax: number | null;
    displayType: DisplayType;
    priceDisplay: PriceDisplay;
    showUnavailableTimes: boolean | null;
    unavailableMessage: string | null;
    unavailableScheduleMessage: string | null;
    showNextDayTimeslotsTill: number | null;
    showCurrentDayTimeslotsAfter: number | null;
}
export interface MultiFactorAuthentication {
    defaultFactor: string | null;
    enabled: boolean;
    factors: Factor[];
}
export interface Factor {
    id: ID;
    object: AuthenticationFactorObject;
    phone: string;
    status: AuthenticationFactorStatus;
}

export enum AuthenticationFactorObject {
    SMS_AUTHENTICATION_FACTOR = "sms_authentication_factor",
}

export enum AuthenticationFactorStatus {
    VERIFIED = "verified",
    PENDING = "pending",
}

export enum PriceDisplay {
    HIGHEST = "highest",
    LOWEST = "lowest",
}

export enum DisplayType {
    COUNT = "count",
    PRICE = "price",
    NONE = "none",
}

export enum EquipmentAllocationStrategy {
    LARGEST_FIRST = "largest_first",
    SMALLEST_FIRST = "smallest_first",
    CUSTOM_ASSIGNMENT = "custom_assignment",
}

export interface PrioritizedResourceID {
    priority: number;
    resource: { id: ID };
}
